import { Box, Divider, Typography, Chip, Stack } from "@mui/material";
import moment from "moment";
import React, { useState, useMemo } from "react";
import styles from "../../Shared/CustomScrollBar.module.css";
import { LedgerStatus } from "../helpers";

interface StatusLogsProps {
  logs: any;
  feature?: String;
}

// Helper function to format ledger status for display
const getFormattedLedgerStatus = (status: string) => {
  switch (status) {
    case LedgerStatus.ACTIVE:
      return "Approved";
    case LedgerStatus.DEACTIVE:
      return "Frozen";
    case LedgerStatus.ROLECHANGED:
      return "Role Changed";
    case "email_changed":
      return "Email Changed";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
};

const StatusLogs = ({ logs, feature = "" }: StatusLogsProps) => {
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Get unique statuses from logs
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(logs?.map((log: any) => log.status));
    return Array.from(statuses);
  }, [logs]);

  // Filter logs based on selected status
  const filteredLogs = useMemo(() => {
    if (!selectedStatus) return logs;
    return logs?.filter((log: any) => log.status === selectedStatus);
  }, [logs, selectedStatus]);

  // Get color for status badge
  const getStatusColor = (status: string) => {
    switch (status) {
      case LedgerStatus.ACTIVE:
        return "#1B8368";
      case LedgerStatus.DEACTIVE:
        return "#FF4949";
      case LedgerStatus.ROLECHANGED:
        return "#4A90E2";
      case "email_changed":
        return "#9B51E0";
      default:
        return "#666666";
    }
  };

  return (
    <Box
      sx={{
        padding: 2,
        width: "100%",
        minHeight: "inherit",
        display: "flex",
        flexDirection: "column",
        justifyContent: logs?.length > 0 ? "flex-start" : "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      {/* Status Filter Badges */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          width: "100%",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Chip
          label="All"
          onClick={() => setSelectedStatus(null)}
          sx={{
            bgcolor: !selectedStatus ? "#1B8368" : "#E0E0E0",
            color: !selectedStatus ? "white" : "black",
            "&:hover": {
              bgcolor: !selectedStatus ? "#1B8368" : "#D0D0D0",
            },
          }}
        />
        {uniqueStatuses.map((status: unknown) => (
          <Chip
            key={status as string}
            label={getFormattedLedgerStatus(status as string)}
            onClick={() => setSelectedStatus(status as string)}
            sx={{
              bgcolor:
                selectedStatus === status
                  ? getStatusColor(status as string)
                  : "#E0E0E0",
              color: selectedStatus === status ? "white" : "black",
              "&:hover": {
                bgcolor:
                  selectedStatus === status
                    ? getStatusColor(status as string)
                    : "#D0D0D0",
              },
            }}
          />
        ))}
      </Stack>

      {filteredLogs?.length > 0 ? (
        filteredLogs
          .slice()
          .reverse()
          .map((item: any, index: number) => (
            <Box sx={{ marginBottom: 2, width: "100%" }} key={index}>
              <Typography
                variant="subtitle2"
                sx={{ textTransform: "capitalize" }}
              >
                {getFormattedLedgerStatus(item.status)} By
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#1B8368",
                  fontWeight: "bold",
                  marginBottom: 1,
                }}
              >
                {item?.adminDetails?.name ?? "Unknown"}
              </Typography>
              <Typography variant="subtitle2" sx={{}}>
                Date & Time
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#1B8368",
                  fontWeight: "bold",
                  marginBottom: 1,
                }}
              >
                {moment
                  .utc(item.createdAt)
                  .local()
                  .format("DD MMM, YYYY | hh:mm A")}
              </Typography>
              <Typography variant="subtitle2" sx={{ marginBottom: "10px" }}>
                Reason of{" "}
                {item.status === LedgerStatus.ACTIVE
                  ? "Approval"
                  : item.status === LedgerStatus.DEACTIVE
                  ? "Rejection"
                  : item.status === LedgerStatus.ROLECHANGED
                  ? "Changing Role"
                  : item.status === "email_changed"
                  ? "Changing Email"
                  : ""}
                {feature && ` (${feature})`}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  marginBottom: 2,
                  borderRadius: 2,
                }}
                className={styles["custom-scrollbar"]}
                style={{
                  maxHeight: item.description.length > 180 ? "200px" : "auto",
                  overflowY: item.description.length > 180 ? "auto" : "hidden",
                }}
              >
                {item.description}
              </Typography>

              {index !== filteredLogs?.length - 1 && (
                <Divider sx={{ borderWidth: "thin", borderColor: "#e6e2e2" }} />
              )}
            </Box>
          ))
      ) : (
        <div>No Logs Found</div>
      )}
    </Box>
  );
};

export default StatusLogs;
